// Chat insights (owner, 29 October 2026): AI reads a business's chats over a chosen period and writes a report with
// counts, where chats stopped, what went unanswered, the next document to add, and advice on the chatbot, marketing
// and demand. `services/flows-llm`, reached through the gateway at `/v1/insights/**`.
//
// Owners and admins only: every route refuses an agent with 403 ADMIN_ROLE_REQUIRED, because reports quote customers
// and spend credits. A report is priced first (`estimateInsights`) and charged to the business's credits as it runs.
//
// Hand-typed until the generated client is next fetched from a running stack. The request path is the house core, so
// the envelope, token refresh and ApiError mapping are the same as everywhere.

import { http } from './_core/request.js';
import type { Envelope, Meta } from './_core/envelope.js';

// ── Error codes ────────────────────────────────────────────────────────────

/** 409: a report is already queued or running for this business. */
export const REPORT_ACTIVE = 'REPORT_ACTIVE';
/** 409: no chat in the period has anything from a customer to read. */
export const NOTHING_TO_READ = 'NOTHING_TO_READ';
/** 402: the business's credit does not cover the estimate. */
export const INSUFFICIENT_CREDITS = 'INSUFFICIENT_CREDITS';
/** 409: the report is not finished, so it cannot be downloaded (or, on cancel, it already finished). */
export const REPORT_NOT_DONE = 'REPORT_NOT_DONE';

// ── Depths and statuses ────────────────────────────────────────────────────

/**
 * How deep a report reads, chosen per report:
 * - `standard`: Claude Haiku reads every chat, Claude Sonnet writes the report;
 * - `thorough`: Claude Sonnet reads and writes;
 * - `expert`: Claude Sonnet reads, Claude Opus writes (offered only while Opus is switched on).
 */
export type InsightDepth = 'standard' | 'thorough' | 'expert';
export const INSIGHT_DEPTHS: readonly InsightDepth[] = ['standard', 'thorough', 'expert'];

export type InsightStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled' | 'skipped';
/** Where a running report is: counting (free), reading chats, organising topics, writing the report. */
export type InsightStage = 'gather' | 'read' | 'organise' | 'write' | 'done';

// ── The estimate ───────────────────────────────────────────────────────────

export interface InsightDepthQuote {
  depth: InsightDepth;
  /** False for `expert` while Claude Opus is switched off. */
  available: boolean;
  reader_model: string;
  writer_model: string;
  /** A model of this depth is switched off and another stands in for it. */
  fallback: boolean;
  estimated_minor: number;
  affordable: boolean;
}

export interface InsightEstimate {
  /** The period as dates in the business's time zone, both days included. */
  from: string;
  to: string;
  /** Chats with any message in the period. */
  chats: number;
  /** Of those, chats with something a customer wrote: the ones AI reads. */
  readable: number;
  /** What this report would read after the cap: a fair sample when `sampled`. */
  to_read: number;
  /** Chats already read by an earlier report, unchanged since: free. */
  reused: number;
  sampled: boolean;
  max_chats: number;
  /** About how long it takes. */
  minutes: number;
  currency: string;
  available_minor: number | null;
  /** False when billing could not price it: the prices below are then 0 and unknown. */
  priced: boolean;
  depths: InsightDepthQuote[];
}

export interface EstimateInsightsOpts {
  from: string;
  to: string;
  channels?: string[];
  max_chats?: number;
  /** How many of the report's own questions it will answer: each adds a little to what reading costs. */
  focus?: number;
}

// ── A report ───────────────────────────────────────────────────────────────

export interface InsightKpis {
  chats: number;
  people: number;
  /** Share of read chats the chatbot fully answered, 0 to 100; null when nothing was read. */
  answered_by_bot_pct: number | null;
  /** Chats where the customer had the last word and nobody replied. */
  unanswered: number;
}

export interface InsightReportSummary {
  id: string;
  status: InsightStatus;
  stage: InsightStage;
  progress_done: number;
  progress_total: number;
  from: string;
  to: string;
  channels: string[];
  depth: InsightDepth;
  reader_model: string;
  writer_model: string;
  focus: string[];
  /** Queued by the monthly schedule rather than by a person. */
  automatic: boolean;
  requested_by: string | null;
  chats: number;
  read: number;
  reused: number;
  sampled: boolean;
  estimated_minor: number;
  spent_minor: number;
  currency: string;
  headline: string | null;
  /** The language the report is written in. */
  language: 'en' | 'af';
  error: string | null;
  kpis: InsightKpis | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface InsightTotals {
  chats: number;
  people: number;
  new_people: number;
  returning_people: number;
  customer_messages: number;
  chatbot_messages: number;
  staff_messages: number;
  /** Threads with only broadcasts in the period and no customer reply: counted, never read. */
  broadcast_only: number;
  taken_over: number;
  /** The customer had the last word and nobody replied. */
  unanswered: number;
  first_reply_median_minutes: number | null;
  first_reply_p90_minutes: number | null;
}

export interface InsightFlowStat {
  flow_id: string;
  name: string;
  starts: number;
  /** Starts by what began them: keyword, intent, greeting, first_contact, default, offer, switch, api. */
  by: Record<string, number>;
  finished: number;
  handed_over: number;
  /** Runs still waiting for the customer, or closed after they went quiet. */
  stopped: number;
  /** Each step in the order the flow runs it, and how many runs reached it. */
  funnel: { node_id: string; label: string; reached: number }[];
  /** The step runs stopped at because the customer never answered it. */
  stop_points: { node_id: string; label: string; count: number }[];
}

export interface InsightMetrics {
  timezone: string;
  totals: InsightTotals;
  /** The same figures for the period of the same length just before. */
  previous: InsightTotals | null;
  per_day: { date: string; chats: number; new_people: number }[];
  /** 7 × 24: chats started by weekday (0 = Sunday) and hour, in the business's time zone. */
  heat: number[][];
  channels: { channel: string; chats: number }[];
  flows: InsightFlowStat[];
  ai_answers: { given: number; answered: number; not_sure: number; not_sure_questions: { question: string; count: number }[] };
  /** The latest deep answer review in the period, by verdict. */
  deep_review: { reviewed_at: string; counts: Record<string, number> } | null;
}

export interface InsightGroup {
  name: string;
  description: string;
}

/** The business's own groups, worked out from what its customers said. Rows use these names. */
export interface InsightTaxonomy {
  topics: InsightGroup[];
  products: InsightGroup[];
  customer_types: InsightGroup[];
  question_themes: InsightGroup[];
  stop_groups: InsightGroup[];
}

/** `refs` are row references (C1, C2, ...): the chats an item is based on. */
export interface InsightSections {
  headline: string;
  summary: string[];
  highlights: string[];
  bot: { assessment: string; strengths: string[]; weaknesses: string[] };
  gaps: { theme: string; count: number; why: string; examples: string[]; refs: string[] }[];
  documents: { title: string; why: string; outline: string[]; answers: string[]; count: number; refs: string[] }[];
  focus: { question: string; answer: string; count: number | null; refs: string[] }[];
  improvements: {
    title: string;
    area: 'chatbot' | 'flows' | 'knowledge' | 'team' | 'hours' | 'other';
    detail: string;
    impact: 'high' | 'medium' | 'low';
    effort: 'high' | 'medium' | 'low';
    evidence: string;
    refs: string[];
  }[];
  marketing: { idea: string; why: string; audience: string; channel: string; timing: string; refs: string[] }[];
  demand: { item: string; signal: string; detail: string; count: number; refs: string[] }[];
  opportunities: { title: string; detail: string; refs: string[] }[];
  risks: { title: string; detail: string; refs: string[] }[];
  next_steps: { title: string; detail: string; refs: string[] }[];
}

export interface InsightReport extends InsightReportSummary {
  metrics: InsightMetrics | null;
  taxonomy: InsightTaxonomy | null;
  sections: InsightSections | null;
}

export type InsightOutcome =
  | 'resolved_by_bot'
  | 'resolved_by_team'
  | 'waiting_for_team'
  | 'abandoned'
  | 'info_only'
  | 'spam';
export type InsightSentiment = 'positive' | 'neutral' | 'negative';
export type InsightBuyingSignal = 'none' | 'some' | 'strong';

export interface InsightQuestion {
  text: string;
  /** The question theme it was grouped under, when it went unanswered or partly answered. */
  theme: string | null;
  answered: 'yes' | 'partly' | 'no';
  by: 'chatbot' | 'staff' | 'nobody';
}

/**
 * One chat in the period. The counted fields are there for every chat; the AI fields only when `read` is true (a
 * broadcast-only thread, or a chat left out of a sample, is counted but not read).
 */
export interface InsightRow {
  /** C1, C2, ...: how the report's text refers to this chat. */
  ref: string;
  conversation_id: string;
  at: string;
  channel: string;
  contact_name: string | null;
  /** The contact book entry, for Tag these people; null for a website visitor or an unknown number. */
  contact_id: string | null;
  returning: boolean;
  customer_messages: number;
  chatbot_messages: number;
  staff_messages: number;
  taken_over: boolean;
  unanswered: boolean;
  first_reply_minutes: number | null;
  /** The flows that ran in this chat in the period. */
  flows: string[];
  read: boolean;
  summary: string | null;
  customer_type: string | null;
  topics: string[];
  products: string[];
  outcome: InsightOutcome | null;
  /** The stop group (from the taxonomy) when the chat ended without what the customer came for. */
  stop_reason: string | null;
  stopped_after: string | null;
  sentiment: InsightSentiment | null;
  buying_signal: InsightBuyingSignal | null;
  buying_detail: string | null;
  complaint: string | null;
  unmet: string | null;
  language: string | null;
  quote: string | null;
  questions: InsightQuestion[];
  /** Answers to the report's own questions, by their index in `focus`. */
  focus: { q: number; answer: 'yes' | 'no' | 'unclear'; detail: string }[];
}

export interface InsightRowsMeta extends Meta {
  /** Pass as `after` for the next page; null on the last page. */
  next_after: string | null;
  total: number;
}

export interface StartInsightReportRequest {
  from: string;
  to: string;
  channels?: string[];
  depth: InsightDepth;
  /** Up to 5 questions of the business's own, each at most 200 characters. */
  focus?: string[];
  max_chats?: number;
  /** What the report is written in: the console's language when it is English or Afrikaans. English otherwise. */
  language?: 'en' | 'af';
}

export interface InsightSettings {
  /** A report on the 1st of each month for the month before. Off by default. */
  monthly: boolean;
  depth: InsightDepth;
  /** Empty means every channel. */
  channels: string[];
  focus: string[];
  /** The most chats one report reads: 50 to 2 000, 500 by default. */
  max_chats: number;
  /** What the monthly report is written in. */
  language: 'en' | 'af';
  next_run_at: string | null;
}

export type SaveInsightSettingsRequest = Partial<Omit<InsightSettings, 'next_run_at'>>;

// ── Calls ──────────────────────────────────────────────────────────────────

/** What a report of this period would read and cost at each depth. */
export function estimateInsights(opts: EstimateInsightsOpts): Promise<Envelope<InsightEstimate>> {
  return http.get<Envelope<InsightEstimate>>('/v1/insights/estimate', {
    query: {
      from: opts.from,
      to: opts.to,
      channels: opts.channels?.length ? opts.channels.join(',') : undefined,
      max_chats: opts.max_chats,
      focus: opts.focus
    }
  });
}

/** Queue a report. 402 INSUFFICIENT_CREDITS; 409 REPORT_ACTIVE or NOTHING_TO_READ; 400 VALIDATION. */
export function startInsightReport(req: StartInsightReportRequest): Promise<Envelope<InsightReportSummary>> {
  return http.post<Envelope<InsightReportSummary>>('/v1/insights/reports', { body: req });
}

/** The business's reports, newest first. */
export function listInsightReports(limit?: number): Promise<Envelope<InsightReportSummary[]>> {
  return http.get<Envelope<InsightReportSummary[]>>('/v1/insights/reports', { query: { limit } });
}

export function getInsightReport(id: string): Promise<Envelope<InsightReport>> {
  return http.get<Envelope<InsightReport>>(`/v1/insights/reports/${encodeURIComponent(id)}`);
}

/** One page of a report's chats, oldest first (at most 1 000). */
export function listInsightRows(
  id: string,
  opts: { after?: string; limit?: number } = {}
): Promise<{ data: InsightRow[]; meta: InsightRowsMeta }> {
  return http.get<{ data: InsightRow[]; meta: InsightRowsMeta }>(`/v1/insights/reports/${encodeURIComponent(id)}/rows`, {
    query: { after: opts.after, limit: opts.limit }
  });
}

/** Every chat of a report, page by page. */
export async function listAllInsightRows(id: string): Promise<InsightRow[]> {
  const rows: InsightRow[] = [];
  let after: string | undefined;
  for (let page = 0; page < 20; page++) {
    const res = await listInsightRows(id, { after, limit: 1000 });
    rows.push(...res.data);
    if (!res.meta.next_after) break;
    after = res.meta.next_after;
  }
  return rows;
}

/** Stop a queued or running report. Only what it already read is charged. */
export function cancelInsightReport(id: string): Promise<Envelope<InsightReportSummary>> {
  return http.post<Envelope<InsightReportSummary>>(`/v1/insights/reports/${encodeURIComponent(id)}/cancel`);
}

export function deleteInsightReport(id: string): Promise<void> {
  return http.delete<void>(`/v1/insights/reports/${encodeURIComponent(id)}`);
}

/** The whole report as a PDF, or every table as an Excel workbook. 409 REPORT_NOT_DONE. */
export function downloadInsightReport(id: string, format: 'pdf' | 'xlsx'): Promise<Blob> {
  return http.getBlob(`/v1/insights/reports/${encodeURIComponent(id)}/export`, { query: { format } });
}

export function getInsightSettings(): Promise<Envelope<InsightSettings>> {
  return http.get<Envelope<InsightSettings>>('/v1/insights/settings');
}

/** Any field left out keeps what is saved. */
export function saveInsightSettings(req: SaveInsightSettingsRequest): Promise<Envelope<InsightSettings>> {
  return http.put<Envelope<InsightSettings>>('/v1/insights/settings', { body: req });
}
